import { Backdrop, Box, Button, CircularProgress, Container, Grid, Paper, ThemeProvider, Typography, createTheme } from '@mui/material'
import { onAuthStateChanged } from 'firebase/auth'
import { useEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { BUILDINFO } from './app/buildinfo'
import { IS_LITE, MAX_AGE_FOR_AUTO_TOUR } from './app/const'
import { useAppDispatch, useAppSelector } from './app/hooks'
import About from './features/about/About'
import AdminOrg from './features/admin-org/AdminOrg'
import AdminOrgEditor from './features/admin-org/AdminOrgEditor'
import AdminOrgUserMng from './features/admin-org/AdminOrgUserMng'
import AdminOrgUserRpt from './features/admin-org/AdminOrgUserRpt'
import AdminSlug from './features/admin-slug/AdminSlug'
import AdminStripe from './features/admin-stripe/AdminStripe'
import AdminStripeEditor from './features/admin-stripe/AdminStripeEditor'
import AdminUser from './features/admin-user/AdminUser'
import AdminUserEditor from './features/admin-user/AdminUserEditor'
import Admin from './features/admin/Admin'
import AdminClearCache from './features/admin/AdminClearCache'
import AdminConfig from './features/admin/AdminConfig'
import AdminConfigEditor from './features/admin/AdminConfigEditor'
import AdminManageCache from './features/admin/AdminManageCache'
import Analysis from './features/analysis/Analysis'
import AnalysisMap from './features/analysis/AnalysisMap'
import { selectDomain, selectDomainCss } from './features/analysis/AnalysisSlice'
import AnalysisWip from './features/analysis/AnalysisWip'
import ApiDocs from './features/api-token/ApiDocs'
import ApiToken from './features/api-token/ApiToken'
import { authInitAsync, authStateChange, fireauth, getUserRolesAsync, remoteConfigAsync, selectAuthInitStatus, selectAuthUser, selectFutureMaintenanceMessage, selectIsMaintenance, selectMaintenanceMessage } from './features/auth/AuthSlice'
import Login from './features/auth/Login'
import LoginWithEmail from './features/auth/LoginWithEmail'
import ProtectedRoute from './features/auth/ProtectedRouter'
import BulkUploadHistory from './features/bulk-upload/BulkUploadHistory'
import BulkUploadr from './features/bulk-upload/BulkUploadr'
import CustomizeHome from './features/customize/CustomizeHome'
import { selectIsPreview } from './features/customize/CustomizeSlice'
import Diagnostics from './features/diagnostics/Diagnostics'
import Faq from './features/faq/Faq'
import Feedback from './features/feedback/Feedback'
import Home from './features/home/Home'
import InviteLandingPage from './features/invite/InviteLandingPage'
import JwtToken from './features/jwt-token/JwtToken'
import Profile from './features/profile/Profile'
import Slugs from './features/slugs/Slugs'
import CurrentSubscription from './features/subscription/CurrentSubscription'
import PricingHome from './features/subscription/PricingHome'
import SubscriptionHome from './features/subscription/SubscriptionHome'
import FooterButton from './features/utils/FooterButton'
import themes from './features/utils/MUITheme'
import PreviewHeader from './features/utils/PreviewHeader'
import ResponsiveAppBar from './features/utils/ResponsiveAppBar'
import Tour from './features/utils/Tour'

let debounceTimer: any

function App() {
  const authUser = useAppSelector(selectAuthUser)
  const authInitStatus = useAppSelector(selectAuthInitStatus)
  const isMaintenaceMode = useAppSelector(selectIsMaintenance)
  const maintenanceMessage = useAppSelector(selectMaintenanceMessage)
  const futureMaintenanceMessage = useAppSelector(selectFutureMaintenanceMessage)
  const userToAssume = localStorage.getItem('userToAssume')
  const dispatch = useAppDispatch()
  const getUserRolesAsyncDebounced = () => {
    dispatch(getUserRolesAsync({}))
  }
  useEffect(() => {
    onAuthStateChanged(fireauth, (usr) => {
      if (usr) {
        dispatch(authStateChange({
          user: {
            uid: usr.uid,
            displayName: usr.displayName,
            email: usr.email,
            photoURL: usr.photoURL,
            providerId: usr.providerId,
            emailVerified: usr.emailVerified,
            authorities: [{ authority: 'ROLE_USER' }],
            paidTier: 0,
            paidTierCount: 0,
            paidTierExpiry: 0,
            orgPaidTierCount: 0,
          }
        }))
        // debounced dispatch
        clearTimeout(debounceTimer)
        debounceTimer = setTimeout(getUserRolesAsyncDebounced, 2500)
      } else {
        clearTimeout(debounceTimer)
      }
    })
  }, [dispatch])

  useEffect(() => {
    dispatch(authInitAsync({}))
    dispatch(remoteConfigAsync({}))
  }, [dispatch])

  useEffect(() => {
    try {
      setShowTour(authUser && authUser.createdTime && new Date(authUser.createdTime).getTime() > new Date().getTime() - MAX_AGE_FOR_AUTO_TOUR ? true : false)
    } catch (e) {
      setShowTour(false)
    }
  }, [dispatch, authUser])

  const domain = useAppSelector(selectDomain)
  const dynaTheme = useMemo(() =>
    createTheme(themes[domain?.toLowerCase() || 'default']),
    [domain],
  )
  const domainCss = useAppSelector(selectDomainCss)

  const isPreview = useAppSelector(selectIsPreview)

  const [showTour, setShowTour] = useState(false)
  const handleCloseTour = () => { setShowTour(false) }

  return (
    <ThemeProvider theme={dynaTheme}><Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <PreviewHeader show={isPreview} />
      {/* Nav Bar */}
      <ResponsiveAppBar onOpenTour={() => {
        localStorage.setItem('tourStatus', '0')
        setShowTour(true)
      }} />

      <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Main Page Content */}
        <Container sx={{ textAlign: 'center', marginBottom: '60px', flex: 1, display: 'flex', flexDirection: 'column' }}>

          {isMaintenaceMode && maintenanceMessage && <Typography variant="h4" component="h1" gutterBottom>{maintenanceMessage}</Typography>}
          {!isMaintenaceMode && futureMaintenanceMessage && <Typography variant="h3" component="h1" gutterBottom>{futureMaintenanceMessage}</Typography>}
          {!!userToAssume && <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'yellow', padding: '10px', borderRadius: '5px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h6" component="h1" gutterBottom>Simulating <strong>{userToAssume}</strong></Typography>
            <Button variant="contained" onClick={() => {
              localStorage.setItem('userToAssume', '')
              window.location.reload()
            }}>Remove Simulation</Button>
          </Box>}
          {!isMaintenaceMode && authInitStatus !== 'loading' && <>
            {IS_LITE ? <Routes>
              <Route path="/analysis">
                <Route path="slug/:slugOrAddress">
                  <Route path="" element={<Analysis />} />
                  <Route path="map" element={<AnalysisMap />} />
                </Route>
                <Route path="addr/:slugOrAddress" element={<AnalysisWip />} />
              </Route>
              <Route path="*" element={<h1>404</h1>} />
            </Routes> : <Routes>
              <Route path="/" element={<Navigate to="/home" />} />
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/loginWithEmail/:loginType" element={<LoginWithEmail />} />
              <Route path="/about" element={<About />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route element={<ProtectedRoute redirectTo="/login" />}> {/* https://stackoverflow.com/a/69592617 */}
                <Route path="/token" element={<JwtToken />} />
                <Route path="/analysis">
                  <Route path="slug/:slugOrAddress">
                    <Route path="" element={<Analysis />} />
                    <Route path="map" element={<AnalysisMap />} />
                  </Route>
                  <Route path="addr/:slugOrAddress" element={<AnalysisWip />} />
                </Route>
                <Route path="/profile" element={<Profile />} />
                <Route path="/bulk" element={<BulkUploadr />} />
                <Route path="/bulk/history/:slug" element={<BulkUploadHistory />} />
                <Route path="/diagnostics" element={<Diagnostics />} />
                <Route path="/admin">
                  <Route path="" element={<Admin />} />
                  <Route path="config/:mode">
                    <Route path="" element={<AdminConfig />} />
                    <Route path=":operation" element={<AdminConfigEditor />} />
                    <Route path=":operation/:key" element={<AdminConfigEditor />} />
                  </Route>
                  <Route path="slugs">
                    <Route path="" element={<Slugs />} />
                    <Route path=":operation/:arg" element={<AdminSlug />} />
                  </Route>
                  <Route path="users">
                    <Route path="" element={<AdminUser />} />
                    <Route path="uid/:uid" element={<AdminUserEditor />} />
                  </Route>
                  <Route path="clearcache" element={<AdminClearCache />}>
                    <Route path="" element={<AdminClearCache />} />
                    <Route path=":slugOrAddress" element={<AdminClearCache />} />
                  </Route>
                  <Route path="managecache" element={<AdminManageCache />}>
                    <Route path="" element={<AdminManageCache />} />
                    <Route path=":slugOrAddress" element={<AdminManageCache />} />
                  </Route>
                  <Route path="orgs">
                    <Route path="" element={<AdminOrg />} />
                    <Route path="manage/:oid" element={<AdminOrgUserMng />} />
                    <Route path=":operation/:oid" element={<AdminOrgEditor />} />
                  </Route>
                  <Route path="stripe">
                    <Route path="" element={<AdminStripe />} />
                    <Route path=":operation/:sid" element={<AdminStripeEditor />} />
                  </Route>
                  <Route path="userRpt">
                    <Route path="" element={<AdminOrgUserRpt />} />
                  </Route>
                </Route>
                <Route path="/api">
                  <Route path="" element={<ApiToken />} />
                  <Route path="docs" element={<ApiDocs />} />
                </Route>
                <Route path="/subscription">
                  <Route path="" element={<SubscriptionHome />} />
                  <Route path="price" element={<PricingHome />} />
                  <Route path="current" element={<CurrentSubscription />} />
                </Route>
                <Route path="/customize">
                  <Route path="" element={<CustomizeHome />} />
                  <Route path=":mode" element={<CustomizeHome />} /> {/* mode=all | me */}
                </Route>
              </Route>
              <Route path="/invited/slug/:slug" element={<InviteLandingPage />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="*" element={<h1>404</h1>} />
            </Routes>}
          </>}
          <Backdrop
            sx={{ backgroundColor: domainCss?.backdropBG, zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={authInitStatus === 'loading'}>
            <CircularProgress />
          </Backdrop>
        </Container>

        {/* Footer */}
        {/* https://stackoverflow.com/a/47071856/234110 */}
        <Container maxWidth="xl" sx={{ height: '50px', boxShadow: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <Box sx={{ textAlign: 'left' }}>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                <FooterButton label="About" href="about" fontWeight="bold" />
                <FooterButton label={BUILDINFO.hash?.substring(0, 7)} href="/token" color="text.secondary" />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Paper>
    </Box>
      <Tour open={showTour} onClose={() => { handleCloseTour() }} />
    </ThemeProvider>
  )
}

export default App
